import React, { SetStateAction, useEffect, useRef, useState } from 'react';
// Local Imports
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { capitalizeFirstLetter, formatDate } from '../../utils/validator';
import { allServicesAPI } from '../../redux/features/services/servicesSlice';
import {
  Button,
  CustomTable,
  ErrorModal,
  Popover,
  SearchBar,
  Spinner,
  Status,
} from '../../components/shared';
import {
  allServicesColumnData,
  allServicesTableColumnExtensions,
} from '../../utils/mockUpData';
import { truncateString } from '../../utils/utils';
import AddNewServiceModal from './AddNewService';
import { TableActions } from '../../components/shared/StatusUtility';
import useOnClickOutside from '../../utils/hooks/useOnClickOutside';
import Children from './utils';

const Services = () => {
  const { loading, error, allServices } = useAppSelector(
    (state) => state.allServices
  );
  const dispatch = useAppDispatch();

  // States
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [isOpenNewServiceModal, setIsOpenNewServiceModal] =
    useState<boolean>(false);
  const [activePopover, setActivePopover] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const rowData = allServices?.data?.map((item: any, index: number) => {
    return {
      index: index + 1,
      id: item?._id,
      serviceName: item?.name ? capitalizeFirstLetter(item?.name) : '',
      description: item?.description
        ? truncateString(item?.description, 4)
        : '',
      price: item?.price ? `$${item?.price}` : 'N/A',
      duration: item?.duration ? `${item?.duration} min` : 'N/A',
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'MM/DD/YY, hh:mm A')
        : '',
      status: item?.status ?? '',
      // Store raw values for edit mode
      rawPrice: item?.price,
      rawDuration: item?.duration,
    };
  });

  // Data Providers
  const dataProviders = [
    {
      columnName: ['status'],
      func: (restProps: any) =>
        Status(restProps?.row?.status, 'Active', 'Inactive'),
    },
    {
      columnName: ['action'],
      func: (restProps: any) => (
        <TableActions
          restProps={restProps}
          children={() => Children(restProps, handleServicesList)}
          isActive={Boolean(activePopover === restProps?.row?.index)}
          onToggle={handleToggle}
          placement='right-20 top-7'
          height='max-h-60'
          ref={popoverRef}
        />
      ),
    },
  ];

  // Toggle Popover
  const handleToggle = (index: number) => {
    setActivePopover((prev) => (prev === index ? null : index));
  };

  // HANDLE SERVICES LIST
  const handleServicesList = () => {
    dispatch(allServicesAPI());
    setActivePopover(null);
  };
  // UseEffect
  useEffect(() => {
    handleServicesList();
    // eslint-disable-next-line
  }, [dispatch]);

  //useOutSideClick
  useOnClickOutside(popoverRef, () => setActivePopover(null));

  // Loading
  if (loading) {
    return <Spinner />;
  }

  return (
    <React.Fragment>
      <div className='w-full h-full flex flex-col gap-2 p-3'>
        <div className='flex gap-2'>
          <div className='min-w-max'>
            <Button
              variant='primary'
              fullWidth
              onClick={() => setIsOpenNewServiceModal(true)}
            >
              Add New Service
            </Button>
          </div>
          <SearchBar
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onClear={() => setSearchName('')}
          />
          <div>
            <Popover menuHeading={'Filters'}></Popover>
          </div>
        </div>

        <ErrorModal showModal={Boolean(error)} errorMsg={error} />

        {/* Table */}
        <div className=''>
          <CustomTable
            rows={rowData?.length > 0 ? rowData : []}
            columns={allServicesColumnData}
            tableColumnExtensions={allServicesTableColumnExtensions}
            dataProviders={dataProviders}
            selectedIds={selectedIds}
            setSelectedIds={(selectedIds: SetStateAction<never[]>) =>
              setSelectedIds(selectedIds)
            }
            componentHeader={true}
          />
        </div>
      </div>

      {/* ADD NEW SERVICE MODAL */}
      <AddNewServiceModal
        isOpen={isOpenNewServiceModal}
        setIsOpen={setIsOpenNewServiceModal}
        from='add'
      />
    </React.Fragment>
  );
};

export default Services;
